import { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';
import { postalFsaPrefix } from './storefront';

/**
 * Service areas are named lists of postal codes. A booking or delivery matches
 * the first three characters (Canadian FSA, or the first three of a US ZIP) —
 * the same rule online booking and area-based delivery pricing already use.
 */
export interface OrderServiceArea {
  id: string;
  name: string;
  prefixes: string[];
}

const areaCache = new Map<string, OrderServiceArea[]>();
const areaInflight = new Map<string, Promise<OrderServiceArea[]>>();

const CA_POSTAL_RE = /[A-Za-z]\d[A-Za-z][\s-]?\d[A-Za-z]\d/;
const US_ZIP_RE = /\b\d{5}(?:-\d{4})?\b/;

function prefixesFromCodes(codes: unknown): string[] {
  if (!Array.isArray(codes)) return [];
  const seen = new Set<string>();
  for (const code of codes) {
    const prefix = postalFsaPrefix(String(code || ''));
    if (prefix.length === 3) seen.add(prefix);
  }
  return [...seen];
}

/** Load a business's active service areas, shared across every order card. */
export function loadServiceAreas(businessId: string): Promise<OrderServiceArea[]> {
  const cached = areaCache.get(businessId);
  if (cached) return Promise.resolve(cached);
  const pending = areaInflight.get(businessId);
  if (pending) return pending;

  const request = (async () => {
    const { data, error } = await supabase
      .from('service_areas')
      .select('id, name, postal_codes, is_active')
      .eq('business_id', businessId)
      .order('name', { ascending: true });
    if (error) throw error;

    const areas: OrderServiceArea[] = (data || [])
      .filter((row) => row.is_active !== false && row.name)
      .map((row) => ({
        id: row.id,
        name: String(row.name),
        prefixes: prefixesFromCodes(row.postal_codes),
      }))
      .filter((area) => area.prefixes.length > 0);

    areaCache.set(businessId, areas);
    return areas;
  })().finally(() => {
    areaInflight.delete(businessId);
  });

  areaInflight.set(businessId, request);
  return request;
}

export function useServiceAreas(businessId: string | null | undefined): OrderServiceArea[] | null {
  const [areas, setAreas] = useState<OrderServiceArea[] | null>(() =>
    businessId ? areaCache.get(businessId) ?? null : null
  );

  useEffect(() => {
    if (!businessId) {
      setAreas(null);
      return;
    }
    const cached = areaCache.get(businessId);
    if (cached) {
      setAreas(cached);
      return;
    }
    let cancelled = false;
    loadServiceAreas(businessId)
      .then((next) => {
        if (!cancelled) setAreas(next);
      })
      .catch((err) => {
        console.error('Failed to load service areas:', err);
        if (!cancelled) setAreas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  return areas;
}

/** Postal on the order: structured checkout snapshot, then a code scraped from the address. */
export function orderPostalCode(order: {
  shipping_address?: { postal_code?: string } | null;
  delivery_address?: string | null;
}): string {
  const structured = order.shipping_address?.postal_code?.trim();
  if (structured) return structured;
  const text = order.delivery_address || '';
  return text.match(CA_POSTAL_RE)?.[0] || text.match(US_ZIP_RE)?.[0] || '';
}

/** Area name for this order, or null when the address isn't in a service area. */
export function orderAreaName(
  order: {
    shipping_address?: { postal_code?: string } | null;
    delivery_address?: string | null;
  },
  areas: OrderServiceArea[] | null | undefined
): string | null {
  if (!areas?.length) return null;
  const prefix = postalFsaPrefix(orderPostalCode(order));
  if (prefix.length < 3) return null;
  return areas.find((area) => area.prefixes.includes(prefix))?.name ?? null;
}
